import { useMemo } from "react";
import { AlertTriangle, Ghost, TrendingDown } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { getMockData } from "../lib/mockData";
import { formatPct } from "../lib/csv";

export default function MeetingRooms() {
  const { meetingRoomStats } = useMemo(() => getMockData(), []);

  const totalCapacity = meetingRoomStats.reduce((s, r) => s + r.capacity, 0);
  const totalUsage = meetingRoomStats.reduce((s, r) => s + r.avgUsage, 0);
  const avgEff = totalUsage / totalCapacity;
  const avgNoShow =
    meetingRoomStats.reduce((s, r) => s + r.noShowRate, 0) /
    meetingRoomStats.length;
  const underused = meetingRoomStats.filter((r) => r.underused);
  const ghostMeetings = meetingRoomStats.filter((r) => r.noShowRate > 0.25);

  return (
    <div data-testid="page-meeting-rooms">
      <PageHeader
        title="Meeting rooms"
        subtitle="Six rooms across three floors. Capacity, real usage and ghost meetings."
        testId="meeting-rooms-header"
      />

      {/* Insight banner */}
      <Card
        className="mb-6 border-amber-200 bg-amber-50 shadow-sm"
        data-testid="banner-insight"
      >
        <CardContent className="flex items-start gap-3 p-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-amber-100 text-amber-700">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <div className="font-heading text-base font-semibold text-amber-900">
              Rooms are booked but not used efficiently
            </div>
            <p className="mt-1 text-sm text-amber-800">
              Average occupancy is {formatPct(avgEff)} of capacity, while booking
              rate stays high. Consider downsizing large rooms or enforcing
              auto-release on no-shows.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* High level KPIs */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="border-slate-200 shadow-sm" data-testid="mr-kpi-efficiency">
          <CardContent className="p-5">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Avg efficiency
            </div>
            <div className="mt-2 font-heading text-3xl font-semibold text-slate-900">
              {formatPct(avgEff)}
            </div>
            <div className="mt-1 text-xs text-slate-500">avg participants / capacity</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm" data-testid="mr-kpi-noshow">
          <CardContent className="p-5">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Avg no-show rate
            </div>
            <div className="mt-2 font-heading text-3xl font-semibold text-slate-900">
              {formatPct(avgNoShow)}
            </div>
            <div className="mt-1 text-xs text-rose-700">
              {ghostMeetings.length} rooms with ghost meetings
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm" data-testid="mr-kpi-underused">
          <CardContent className="p-5">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Underused rooms
            </div>
            <div className="mt-2 font-heading text-3xl font-semibold text-slate-900">
              {underused.length} / {meetingRoomStats.length}
            </div>
            <div className="mt-1 text-xs text-slate-500">below 50% capacity</div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="mt-6 border-slate-200 shadow-sm" data-testid="table-rooms">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-heading text-lg font-medium text-slate-900">
            Room-by-room breakdown
          </CardTitle>
          <p className="text-xs text-slate-500">
            Two rooms per floor · last 60 days
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Room</TableHead>
                <TableHead>Floor</TableHead>
                <TableHead className="text-right">Capacity</TableHead>
                <TableHead className="text-right">Avg occupancy</TableHead>
                <TableHead className="text-right">Booking rate</TableHead>
                <TableHead className="text-right">No-show rate</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {meetingRoomStats.map((room) => {
                const isUnderused = room.underused;
                const isGhost = room.noShowRate > 0.25;
                return (
                  <TableRow key={room.id} data-testid={`mr-row-${room.id}`}>
                    <TableCell className="font-medium text-slate-900">
                      Room {room.name}
                    </TableCell>
                    <TableCell>Floor {room.floor}</TableCell>
                    <TableCell className="text-right">{room.capacity}</TableCell>
                    <TableCell className="text-right">
                      {room.avgUsage} / {room.capacity}
                      <span className="ml-2 text-xs text-slate-500">
                        ({formatPct(room.efficiency)})
                      </span>
                    </TableCell>
                    <TableCell className="text-right">{formatPct(room.bookingRate)}</TableCell>
                    <TableCell className="text-right">
                      <span className={isGhost ? "font-semibold text-rose-700" : ""}>
                        {formatPct(room.noShowRate)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {isUnderused && (
                          <Badge variant="secondary" className="bg-amber-50 text-amber-700">
                            <TrendingDown className="mr-1 h-3 w-3" />
                            Underused
                          </Badge>
                        )}
                        {isGhost && (
                          <Badge variant="secondary" className="bg-rose-50 text-rose-700">
                            <Ghost className="mr-1 h-3 w-3" />
                            Ghost
                          </Badge>
                        )}
                        {!isUnderused && !isGhost && (
                          <Badge variant="secondary" className="bg-emerald-50 text-emerald-700">
                            Healthy
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
